import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { LoaderCircle, MoreVertical, RefreshCw, ShieldCheck, Trash2, User, UserPlus } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { SearchInput } from '@/_shared/components/SearchInput';
import { Table, type TableColumn } from '@/_shared/components/Table';
import { Avatar, AvatarFallback, AvatarImage } from '@/_shared/components/ui/avatar';
import { Badge } from '@/_shared/components/ui/badge';
import { Button } from '@/_shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/_shared/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/_shared/components/ui/dropdown-menu';
import type { WorkspaceRole } from '@/_shared/contexts/WorkspaceContext';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useAuth } from '@/_shared/hooks/useAuth';
import { useOrpc } from '@/_shared/hooks/useOrpc';

import { InviteMemberFields } from '../components/InviteMemberFields';
import { type InviteRole, useInviteMember } from '../hooks/useInviteMember';

function roleBadgeVariant(role: WorkspaceRole): 'highlight' | 'warning' | 'info' {
  if (role === 'owner') return 'highlight';
  if (role === 'admin') return 'warning';
  return 'info';
}

// A confirmation the user must accept before a destructive/role-changing action runs.
type PendingAction =
  | { variant: 'make-admin' | 'make-member' | 'remove'; userId: string; name: string }
  | { variant: 'revoke'; inviteId: string };

const ACTION_CONFIG: Record<
  PendingAction['variant'],
  { title: string; confirmLabel: string; destructive: boolean; describe: (name: string) => string }
> = {
  'make-admin': {
    title: 'Make admin',
    confirmLabel: 'Make admin',
    destructive: false,
    describe: (name) => `${name} will be able to manage members, settings, sources, and invites.`,
  },
  'make-member': {
    title: 'Make member',
    confirmLabel: 'Make member',
    destructive: false,
    describe: (name) => `${name} will lose admin access to settings, sources, and member management.`,
  },
  remove: {
    title: 'Remove member',
    confirmLabel: 'Remove',
    destructive: true,
    describe: (name) => `${name} will lose access to this workspace. They can rejoin with a new invite.`,
  },
  revoke: {
    title: 'Delete invite',
    confirmLabel: 'Delete invite',
    destructive: true,
    describe: () =>
      'This invitation will be cancelled immediately. The invited person will no longer be able to accept it.',
  },
};

export function SettingsMembers(): React.ReactElement {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { isAdmin } = useWorkspace();
  const orpc = useOrpc();

  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<InviteRole>('member');
  const [memberSearch, setMemberSearch] = useState('');
  const [pending, setPending] = useState<PendingAction | null>(null);

  const resetInviteForm = useCallback(() => {
    setInviteEmail('');
    setInviteRole('member');
  }, []);

  const { data, isLoading } = useQuery(orpc.workspace.get.queryOptions({ input: {} }));

  const { data: invitesData } = useQuery({
    ...orpc.workspace.listInvites.queryOptions({ input: { filter: 'active_pending' } }),
    enabled: isAdmin,
  });

  const members = useMemo(() => data?.members ?? [], [data]);
  const filteredMembers = useMemo(() => {
    const q = memberSearch.trim().toLowerCase();
    if (!q) return members;
    return members.filter((m) => {
      const name = (m.user.displayName ?? m.user.githubLogin ?? '').toLowerCase();
      const email = (m.user.email ?? '').toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [members, memberSearch]);
  const invites = useMemo(() => invitesData?.invites ?? [], [invitesData]);

  const inviteMember = useInviteMember({
    onSent: () => {
      setInviteDialogOpen(false);
      resetInviteForm();
    },
  });

  const resendInviteMutation = useMutation(
    orpc.workspace.resendInvite.mutationOptions({
      onSuccess: () => {
        toast.success('Invitation resent');
        void queryClient.invalidateQueries({
          queryKey: orpc.workspace.listInvites.queryOptions({ input: { filter: 'active_pending' } }).queryKey,
        });
      },
      onError: (err) => toast.error(err.message || 'Failed to resend invitation'),
    }),
  );

  const revokeInviteMutation = useMutation(
    orpc.workspace.revokeInvite.mutationOptions({
      onSuccess: () => {
        toast.success('Invite revoked');
        void queryClient.invalidateQueries({
          queryKey: orpc.workspace.listInvites.queryOptions({ input: { filter: 'active_pending' } }).queryKey,
        });
      },
      onError: () => toast.error('Failed to revoke invite'),
    }),
  );

  const removeMemberMutation = useMutation(
    orpc.workspace.removeMember.mutationOptions({
      onSuccess: () => {
        toast.success('Member removed');
        void queryClient.invalidateQueries({
          queryKey: orpc.workspace.get.queryOptions({ input: {} }).queryKey,
        });
      },
      onError: () => toast.error('Failed to remove member'),
    }),
  );

  const updateRoleMutation = useMutation(
    orpc.workspace.updateMemberRole.mutationOptions({
      onSuccess: () => {
        toast.success('Role updated');
        void queryClient.invalidateQueries({
          queryKey: orpc.workspace.get.queryOptions({ input: {} }).queryKey,
        });
      },
      onError: (err) => toast.error(err.message || 'Failed to update role'),
    }),
  );

  const actionPending =
    removeMemberMutation.isPending ||
    updateRoleMutation.isPending ||
    revokeInviteMutation.isPending ||
    resendInviteMutation.isPending;

  const confirmAction = (): void => {
    if (!pending) return;
    const closeDialog = { onSuccess: () => setPending(null) };
    switch (pending.variant) {
      case 'make-admin':
        updateRoleMutation.mutate({ userId: pending.userId, role: 'admin' }, closeDialog);
        break;
      case 'make-member':
        updateRoleMutation.mutate({ userId: pending.userId, role: 'member' }, closeDialog);
        break;
      case 'remove':
        removeMemberMutation.mutate({ userId: pending.userId }, closeDialog);
        break;
      case 'revoke':
        revokeInviteMutation.mutate({ inviteId: pending.inviteId }, closeDialog);
        break;
    }
  };

  const handleSendInvite = useCallback(() => {
    inviteMember.send(inviteEmail, inviteRole);
  }, [inviteEmail, inviteRole, inviteMember]);

  const action = pending ? ACTION_CONFIG[pending.variant] : null;
  const actionName = pending && pending.variant !== 'revoke' ? pending.name : '';

  const memberColumns = useMemo<TableColumn<(typeof members)[number]>[]>(
    () => [
      {
        id: 'member',
        header: 'Member',
        sortable: false,
        cell: (member) => {
          const name = member.user.displayName ?? member.user.githubLogin ?? 'user';
          return (
            <div className="flex min-w-0 items-center gap-3">
              <Avatar className="size-8 shrink-0">
                <AvatarImage src={member.user.avatarUrl ?? member.user.githubAvatarUrl ?? undefined} alt={name} />
                <AvatarFallback>{name[0]?.toUpperCase()}</AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <div className="flex min-w-0 items-center gap-2">
                  <p className="truncate text-sm font-medium text-foreground">{name}</p>
                  {member.userId === user?.id ? (
                    <Badge variant="outline" className="shrink-0 tracking-wider uppercase">
                      You
                    </Badge>
                  ) : null}
                </div>
                {member.user.email ? (
                  <p className="truncate text-xs text-muted-foreground">{member.user.email}</p>
                ) : null}
              </div>
            </div>
          );
        },
      },
      {
        id: 'role',
        header: 'Role',
        align: 'right',
        width: '7rem',
        sortable: false,
        cell: (member) => (
          <Badge variant={roleBadgeVariant(member.role)} className="tracking-wider uppercase">
            {member.role}
          </Badge>
        ),
      },
      ...(isAdmin
        ? [
            {
              id: 'actions',
              header: '',
              headerAriaLabel: 'Actions',
              align: 'right',
              width: '3.5rem',
              sortable: false,
              cell: (member) => {
                const isSelf = member.userId === user?.id;
                const isMemberOwner = member.role === 'owner';
                const canManage = !isSelf && !isMemberOwner;
                if (!canManage) return null;
                const name = member.user.displayName ?? member.user.githubLogin ?? 'user';
                return (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="tertiary" iconOnly Icon={<MoreVertical />} aria-label={`Actions for ${name}`} />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {member.role === 'member' ? (
                        <DropdownMenuItem
                          onSelect={() => setPending({ variant: 'make-admin', userId: member.userId, name })}
                        >
                          <ShieldCheck className="size-4" />
                          Make admin
                        </DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem
                          onSelect={() => setPending({ variant: 'make-member', userId: member.userId, name })}
                        >
                          <User className="size-4" />
                          Make member
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        variant="destructive"
                        onSelect={() => setPending({ variant: 'remove', userId: member.userId, name })}
                      >
                        <Trash2 className="size-4" />
                        Remove
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              },
            } as TableColumn<(typeof members)[number]>,
          ]
        : []),
    ],
    [user?.id, isAdmin],
  );

  const inviteColumns = useMemo<TableColumn<(typeof invites)[number]>[]>(
    () => [
      {
        id: 'email',
        header: 'Email',
        sortable: false,
        cell: (invite) => (
          <span className="block truncate text-sm" title={invite.email}>
            {invite.email}
          </span>
        ),
      },
      {
        id: 'role',
        header: 'Role',
        width: '7rem',
        sortable: false,
        cell: (invite) => (
          <Badge variant={roleBadgeVariant(invite.role)} className="tracking-wider uppercase">
            {invite.role}
          </Badge>
        ),
      },
      {
        id: 'lastSentAt',
        header: 'Sent',
        width: '8rem',
        sorter: 'timestamp',
        cell: (invite) => dayjs(invite.lastSentAt).format('MMM D, YYYY'),
      },
      {
        id: 'expiresAt',
        header: 'Expiry',
        width: '8rem',
        sorter: 'timestamp',
        cell: (invite) =>
          new Date(invite.expiresAt) < new Date() ? 'Expired' : dayjs(invite.expiresAt).format('MMM D, YYYY'),
      },
      {
        id: 'actions',
        header: '',
        headerAriaLabel: 'Actions',
        align: 'right',
        width: '3.5rem',
        sortable: false,
        cell: (invite) => {
          const expired = new Date(invite.expiresAt) < new Date();
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="tertiary"
                  iconOnly
                  Icon={<MoreVertical />}
                  aria-label={`Invite actions for ${invite.email}`}
                />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  disabled={expired || resendInviteMutation.isPending}
                  onSelect={() => resendInviteMutation.mutate({ inviteId: invite.id })}
                >
                  <RefreshCw className="size-4" />
                  Resend invitation
                </DropdownMenuItem>
                <DropdownMenuItem
                  variant="destructive"
                  onSelect={() => setPending({ variant: 'revoke', inviteId: invite.id })}
                >
                  <Trash2 className="size-4" />
                  Delete invite
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    [resendInviteMutation],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <LoaderCircle size={24} className="animate-spin" />
      </div>
    );
  }

  return (
    <>
      <section className="mb-8">
        <div className="mb-4 flex items-center justify-between gap-4">
          <h2 className="text-sm font-bold tracking-wider text-muted-foreground uppercase">Members</h2>
          {members.length > 0 && (
            <SearchInput
              value={memberSearch}
              onChange={setMemberSearch}
              placeholder="Search members..."
              className="w-full max-w-xs shrink-0"
            />
          )}
        </div>
        <Table
          data={filteredMembers}
          columns={memberColumns}
          getRowId={(m) => m.userId}
          hideHeader
          fixedLayout
          maxHeight="24rem"
          emptyState={memberSearch.trim() ? 'No matching members.' : 'No results'}
        />
      </section>

      {isAdmin && (
        <section className="mb-8">
          <div className="mb-4 flex items-center justify-between gap-4">
            <h2 className="text-sm font-bold tracking-wider text-muted-foreground uppercase">Invites</h2>
            <Button
              variant="primary"
              size="md"
              Icon={<UserPlus />}
              onClick={() => setInviteDialogOpen(true)}
              label="Invite"
            />
          </div>

          <Table
            data={invites}
            columns={inviteColumns}
            getRowId={(i) => i.id}
            enableSort
            fixedLayout
            emptyState="No pending invitations"
            maxHeight="20rem"
          />
        </section>
      )}

      <Dialog
        open={inviteDialogOpen}
        onOpenChange={(open) => {
          setInviteDialogOpen(open);
          if (!open) resetInviteForm();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite member</DialogTitle>
            <DialogDescription>Send an invitation to join this workspace.</DialogDescription>
          </DialogHeader>
          <InviteMemberFields
            idPrefix="invite"
            email={inviteEmail}
            onEmailChange={setInviteEmail}
            role={inviteRole}
            onRoleChange={setInviteRole}
          />
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => setInviteDialogOpen(false)} label="Cancel" />
            <Button
              type="button"
              variant="primary"
              Icon={<UserPlus />}
              loading={inviteMember.isPending}
              onClick={handleSendInvite}
              label="Send invite"
            />
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pending} onOpenChange={(open) => !open && setPending(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{action?.title}</DialogTitle>
            <DialogDescription>{action?.describe(actionName)}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => setPending(null)} label="Cancel" />
            <Button
              type="button"
              variant={action?.destructive ? 'primary-destructive' : 'primary'}
              loading={actionPending}
              onClick={confirmAction}
              label={action?.confirmLabel}
            />
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
